/**
 * Unit Price Block — VitaDoza
 *
 * ÇALIŞMA MANTIĞI
 *
 * 1. Aktif varyantın fiyatını kullanır.
 * 2. Aktif varyantın miktar metafield'ı varsa onu kullanır.
 * 3. Varyant miktarı boşsa ürün seviyesindeki miktar kullanılır.
 * 4. Miktar metafield'ından yalnızca sayısal değer alınır.
 * 5. Birim fiyat kısa birimle gösterilir:
 *    ₺45,65 / 1 kapsül
 * 6. Paket içeriği kısa birimle gösterilir:
 *    60 kapsül
 * 7. 150 ml ve 150ml gibi tüm ML yazımları hariç tutulur.
 * 8. ML varyanttan sayılabilir varyanta geçildiğinde blok yeniden görünür.
 */

(function () {
  'use strict';

  const BLOCK_SELECTOR =
    '.unit-price-block';

  const VARIANT_DATA_SELECTOR =
    '.unit-price-block__variant-data';

  /**
   * Miktar değerinin hesaplama için uygun olup
   * olmadığını kontrol eder.
   *
   * Şu değerlerin tamamı hariç tutulur:
   * 150 ml
   * 150ml
   * 150 ML
   * 150ML
   *
   * @param {string} raw
   * @returns {boolean}
   */
  function isSupportedQuantity(raw) {
    if (
      !raw ||
      typeof raw !== 'string'
    ) {
      return false;
    }

    return !/ml/i.test(
      raw.trim()
    );
  }

  /**
   * Metin içindeki ilk sayısal değeri çıkarır.
   *
   * Örnekler:
   * 60 Adet       -> 60
   * 60 Kapsül     -> 60
   * 300 gr        -> 300
   * 1.500 gram    -> 1500
   * 1,5 gr        -> 1.5
   * 1.500,50 gr   -> 1500.5
   *
   * @param {string} raw
   * @returns {number|null}
   */
  function parseQuantity(raw) {
    if (
      !raw ||
      typeof raw !== 'string'
    ) {
      return null;
    }

    const match =
      raw.match(/[\d.,]+/);

    if (!match) {
      return null;
    }

    let value =
      match[0].trim();

    if (
      value.includes(',') &&
      value.includes('.')
    ) {
      value = value
        .replace(/\./g, '')
        .replace(',', '.');
    } else if (
      value.includes(',')
    ) {
      value =
        value.replace(',', '.');
    } else {
      value = value.replace(
        /\.(?=\d{3}(?:\D|$))/g,
        ''
      );
    }

    const number =
      Number.parseFloat(value);

    if (
      !Number.isFinite(number) ||
      number <= 0
    ) {
      return null;
    }

    return number;
  }

  /**
   * Fiyat değerini güvenli biçimde sayıya çevirir.
   *
   * Shopify fiyatları cent biçimindedir.
   *
   * @param {*} price
   * @returns {number|null}
   */
  function parsePrice(price) {
    const parsedPrice =
      Number(price);

    if (
      !Number.isFinite(parsedPrice) ||
      parsedPrice <= 0
    ) {
      return null;
    }

    return parsedPrice;
  }

  /**
   * Shopify cent değerini Türkçe fiyat formatına çevirir.
   *
   * @param {number} cents
   * @returns {string}
   */
  function formatPrice(cents) {
    return (cents / 100).toLocaleString(
      'tr-TR',
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    );
  }

  /**
   * Paket içeriği metnini oluşturur.
   *
   * Örnek:
   * quantity: 60
   * packageLabel: kapsül
   *
   * Sonuç:
   * 60 kapsül
   *
   * @param {number} quantity
   * @param {string} packageLabel
   * @returns {string}
   */
  function formatPackageContent(
    quantity,
    packageLabel
  ) {
    const formattedQuantity =
      quantity.toLocaleString(
        'tr-TR',
        {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }
      );

    return (
      formattedQuantity +
      ' ' +
      packageLabel
    ).trim();
  }

  /**
   * Liquid tarafından oluşturulan varyant verisini okur.
   * Sonuç blok üzerinde önbelleğe alınır.
   *
   * @param {HTMLElement} block
   * @returns {Object}
   */
  function getVariantData(block) {
    if (
      block._unitPriceVariantData
    ) {
      return block._unitPriceVariantData;
    }

    const script =
      block.querySelector(
        VARIANT_DATA_SELECTOR
      );

    if (!script) {
      block._unitPriceVariantData = {};
      return block._unitPriceVariantData;
    }

    try {
      block._unitPriceVariantData =
        JSON.parse(
          script.textContent || '{}'
        );
    } catch (error) {
      console.warn(
        'VitaDoza birim fiyat varyant verisi okunamadı.',
        error
      );

      block._unitPriceVariantData = {};
    }

    return block._unitPriceVariantData;
  }

  /**
   * Varyant ID'sine göre Liquid'den gelen
   * fiyat ve miktar kaydını bulur.
   *
   * @param {HTMLElement} block
   * @param {string|number|null} variantId
   * @returns {Object|null}
   */
  function getVariantRecord(
    block,
    variantId
  ) {
    if (
      variantId === null ||
      variantId === undefined ||
      variantId === ''
    ) {
      return null;
    }

    const variantData =
      getVariantData(block);

    return (
      variantData[String(variantId)] ||
      null
    );
  }

  /**
   * Aktif varyanta göre hesaplama verisini çözer.
   *
   * Fiyat önceliği:
   * 1. Event içindeki varyant fiyatı
   * 2. Liquid varyant JSON fiyatı
   * 3. Bloktaki data-price
   *
   * Miktar önceliği:
   * 1. Liquid varyant JSON miktarı
   * 2. Bloktaki data-adet-raw
   *
   * @param {HTMLElement} block
   * @param {Object|null} variant
   * @returns {{
   *   variantId: string|null,
   *   price: number|null,
   *   quantityRaw: string
   * }}
   */
  function resolveCalculationData(
    block,
    variant
  ) {
    const eventVariantId =
      variant &&
      variant.id !== undefined
        ? variant.id
        : null;

    const selectedVariantId =
      eventVariantId ||
      block.dataset.selectedVariantId ||
      null;

    const variantRecord =
      getVariantRecord(
        block,
        selectedVariantId
      );

    const eventPrice =
      variant
        ? parsePrice(variant.price)
        : null;

    const recordPrice =
      variantRecord
        ? parsePrice(variantRecord.price)
        : null;

    const datasetPrice =
      parsePrice(
        block.dataset.price
      );

    const price =
      eventPrice ||
      recordPrice ||
      datasetPrice;

    const recordQuantity =
      variantRecord &&
      typeof variantRecord.quantityRaw === 'string'
        ? variantRecord.quantityRaw
        : '';

    const quantityRaw =
      recordQuantity ||
      block.dataset.adetRaw ||
      '';

    return {
      variantId:
        selectedVariantId !== null
          ? String(selectedVariantId)
          : null,

      price: price,

      quantityRaw: quantityRaw,
    };
  }

  /**
   * Bloğu tamamen gizler.
   *
   * DOM'dan kaldırılmaz.
   * Kullanıcı desteklenen başka bir varyanta geçerse
   * blok yeniden görünür.
   *
   * @param {HTMLElement} block
   */
  function hideBlock(block) {
    block.dataset.ready =
      'false';

    block.hidden =
      true;
  }

  /**
   * Bloğu görünür hâle getirir.
   *
   * @param {HTMLElement} block
   */
  function showBlock(block) {
    block.hidden =
      false;

    block.dataset.ready =
      'true';
  }

  /**
   * Tek bir birim fiyat bloğunu hesaplar ve günceller.
   *
   * @param {HTMLElement} block
   * @param {Object|null} variant
   */
  function updateBlock(
    block,
    variant
  ) {
    if (
      !(block instanceof HTMLElement)
    ) {
      return;
    }

    const calculationData =
      resolveCalculationData(
        block,
        variant
      );

    const quantityRaw =
      calculationData.quantityRaw;

    const priceCents =
      calculationData.price;

    if (
      !isSupportedQuantity(
        quantityRaw
      )
    ) {
      hideBlock(block);
      return;
    }

    const quantity =
      parseQuantity(
        quantityRaw
      );

    if (
      !quantity ||
      !priceCents
    ) {
      hideBlock(block);
      return;
    }

    const unit =
      String(
        block.dataset.birim ||
        'adet'
      ).trim();

    const packageLabel =
      String(
        block.dataset.paketLabel ||
        unit
      ).trim();

    const currency =
      block.dataset.currency ||
      '₺';

    const amountElement =
      block.querySelector(
        '.unit-price-block__amount'
      );

    const unitElement =
      block.querySelector(
        '.unit-price-block__unit'
      );

    const basisElement =
      block.querySelector(
        '.unit-price-block__basis'
      );

    const unitPriceCents =
      priceCents / quantity;

    if (amountElement) {
      amountElement.textContent =
        currency +
        formatPrice(
          unitPriceCents
        );
    }

    if (unitElement) {
      unitElement.textContent =
        '/ 1 ' + unit;
    }

    if (basisElement) {
      basisElement.textContent =
        formatPackageContent(
          quantity,
          packageLabel
        );
    }

    block.dataset.price =
      String(priceCents);

    block.dataset.adetRaw =
      quantityRaw;

    if (
      calculationData.variantId
    ) {
      block.dataset.selectedVariantId =
        calculationData.variantId;
    }

    showBlock(block);
  }

  /**
   * Belirtilen alan içindeki tüm blokları başlatır.
   *
   * @param {Document|HTMLElement} scope
   */
  function initBlocks(scope) {
    const root =
      scope || document;

    if (
      root instanceof HTMLElement &&
      root.matches(BLOCK_SELECTOR)
    ) {
      updateBlock(
        root,
        null
      );
    }

    root
      .querySelectorAll(BLOCK_SELECTOR)
      .forEach(function (block) {
        updateBlock(
          block,
          null
        );
      });
  }

  /**
   * Event içindeki varyant nesnesini bulur.
   *
   * @param {Event} event
   * @returns {Object|null}
   */
  function getEventVariant(event) {
    const detail =
      event.detail || {};

    return (
      detail.variant ||
      detail.currentVariant ||
      detail.selectedVariant ||
      null
    );
  }

  /**
   * Event'in ait olduğu ürün alanını belirler.
   *
   * @param {Event} event
   * @returns {Document|HTMLElement}
   */
  function getEventScope(event) {
    if (
      event.target instanceof HTMLElement
    ) {
      return (
        event.target.closest(
          [
            'product-info',
            '.product__info-container',
            '[data-product-id]',
            '.product',
            '.shopify-section',
          ].join(',')
        ) || document
      );
    }

    return document;
  }

  /**
   * Belirtilen kapsam içindeki blokları döndürür.
   *
   * Kapsamın kendisi blok ise onu da dahil eder.
   *
   * @param {Document|HTMLElement} scope
   * @returns {HTMLElement[]}
   */
  function getBlocksInScope(scope) {
    const blocks = [];

    if (
      scope instanceof HTMLElement &&
      scope.matches(BLOCK_SELECTOR)
    ) {
      blocks.push(scope);
    }

    scope
      .querySelectorAll(BLOCK_SELECTOR)
      .forEach(function (block) {
        blocks.push(block);
      });

    return blocks;
  }

  /**
   * Varyant değişikliğinde fiyat ve miktarı günceller.
   *
   * @param {Event} event
   */
  function handleVariantChange(event) {
    const variant =
      getEventVariant(event);

    if (!variant) {
      return;
    }

    const scope =
      getEventScope(event);

    getBlocksInScope(scope)
      .forEach(function (block) {
        updateBlock(
          block,
          variant
        );
      });
  }

  /**
   * Event listener'ları yalnızca bir kez bağlar.
   */
  function bindEvents() {
    const documentElement =
      document.documentElement;

    if (
      documentElement.dataset
        .unitPriceEventsBound ===
      'true'
    ) {
      return;
    }

    documentElement.dataset
      .unitPriceEventsBound =
      'true';

    document.addEventListener(
      'variant:change',
      handleVariantChange
    );

    document.addEventListener(
      'variantChange',
      handleVariantChange
    );

    document.addEventListener(
      'shopify:section:load',
      function (event) {
        if (
          event.target instanceof HTMLElement
        ) {
          initBlocks(
            event.target
          );
        }
      }
    );
  }

  /**
   * Bileşeni başlatır.
   */
  function init() {
    initBlocks(document);
    bindEvents();
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true,
      }
    );
  } else {
    init();
  }
})();